'use client';

// Optimization Changes Flag
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { z } from 'zod';

import { encryptPasswordAction } from '@/app/actions/encryptPassword';
import CheckIcon from '@/assets/icons/check-gray.svg';
import VerifiedTick from '@/assets/icons/verified-tick.svg';
import AlertCircle from '@/assets/images/alert-circle.svg';
import Button from '@/components/V2/Button';
import ButtonLink from '@/components/V2/ButtonLink';
import PasswordInput from '@/components/V2/PasswordInput/PasswordInput';
import Spinner from '@/components/V2/PetProfile/Spinner';
import AuthToast from '@/components/V2/Popup/AuthToast';
import TextInput from '@/components/V2/TextInput';
import { getClientEnv } from '@/config/clientEnv';
import useForm from '@/helpers/hooks/useForm';
import useWebAlias from '@/helpers/hooks/useWebalias';
import { removeSessionCookie, setSessionCookie } from '@/helpers/utils/idleTrackingUtils';
import { AUTH_MODAL_TOAST_CONTAINER } from '@/layouts/ToastWrapper/containerIds';
import { useAppDispatch } from '@/store';
import { useLoginMutation } from '@/store/api/authentication';
import { APPLE, FACEBOOK, GOOGLE } from '@/types/Common';
import { CustomerStatus } from '@/types/CustomerStatus';
import { CustomerType } from '@/types/CustomerType';

const AppleSignInButton = dynamic(() => import('@/components/V2/SocialSignIn/AppleSignInButton'), {
  loading: () => <SocialButtonPlaceholder />,
  ssr: false,
});
const FacebookSignInButton = dynamic(() => import('@/components/V2/SocialSignIn/FacebookSignInButton'), {
  loading: () => <SocialButtonPlaceholder />,
  ssr: false,
});
const GoogleSignInButton = dynamic(() => import('@/components/V2/SocialSignIn/GoogleSignInButton'), {
  loading: () => <SocialButtonPlaceholder />,
  ssr: false,
});

function SocialButtonPlaceholder() {
  return <div className="v2-w-full v2-h-11 v2-bg-gray-100 v2-rounded-xs v2-animate-pulse" />;
}

interface LoginFormProps {
  onSubmit?: (loginData: {
    rememberMe: boolean;
    returnUrl?: string | null;
    shouldShow2FA?: boolean;
    shouldEnter2FA?: boolean;
    customerTypeID?: number;
  }) => void;
  onToggleSignup?: () => void;
  isBackoffice?: boolean;
  onError?: (message: string) => void;
  onThirdPartyLogin?: (handler: (provider: number, token: string) => Promise<void>) => void;
  onThirdPartyNewUser?: (userData: {
    token: string;
    type: string;
    email: string;
    firstName: string;
    lastName: string;
    picture: string;
  }) => void;
}

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;
type LoginFormErrors = Partial<Record<keyof LoginFormData, string>>;

const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  onToggleSignup = () => {},
  isBackoffice = false,
  onError,
  onThirdPartyLogin,
  onThirdPartyNewUser,
}) => {
  const { webalias, generateURL } = useWebAlias();
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [loginMutation] = useLoginMutation();
  const [rememberMe, setRememberMe] = useState(false);
  const LOGIN_FAILED_ERROR = 'Login failed';
  const SHOW_ERROR_STATE_ONLY = ' '; // Space to show red border but no visible message

  // Reset UI state after returning from 2FA (one-time, minimal change)
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (localStorage.getItem('__2fa_reset_login_verified') === 'true') {
      setIsVerified(false);
      setIsSubmitting(false);
      try {
        localStorage.removeItem('__2fa_reset_login_verified');
      } catch (e) {}
    }
    localStorage.removeItem('forgotPassword');
  }, [searchParams]);

  const initialState = useMemo(
    () => ({
      email: '',
      password: '',
    }),
    []
  );

  const {
    data,
    errors,
    handleChange,
    onSubmit: handleFormSubmit,
    setFormErrors,
  } = useForm<LoginFormData, LoginFormErrors>({
    initialState,
    errors: {} as LoginFormErrors,
    validationSchema: loginSchema as any,
  });

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleSubmit = async () => {
    handleFormSubmit({ preventDefault: () => {} } as React.FormEvent<HTMLFormElement>, async () => {
      try {
        // Clear the one-time reset flag when user tries to sign in again
        try {
          if (typeof window !== 'undefined') {
            localStorage.removeItem('__2fa_reset_login_verified');
          }
        } catch (e) {}
        // Encrypt the password using Server Action
        setIsSubmitting(true);
        const encryptedPassword = await encryptPasswordAction(data.password);
        if (!encryptedPassword) {
          const errorMessage = 'Failed to encrypt password. Please try again.';
          // Set form errors to show error states on fields
          setFormErrors({
            email: 'An error occurred. Please try again.',
            password: 'An error occurred. Please try again.',
          });
          if (onError) {
            onError(errorMessage);
          }
          setIsSubmitting(false);
          return;
        }

        const response = await loginMutation({
          loginName: data.email,
          password: encryptedPassword,
          rememberMe,
        }).unwrap();

        console.log(response, 'response from login'); // --- IGNORE ---

        console.log('🔐 [LoginForm] Login successful, checking if session cookie needed...');
        console.log('🔐 [LoginForm] rememberMe:', rememberMe);
        console.log('🔐 [LoginForm] response.success:', response.success);
        console.log('🔐 [LoginForm] response.data exists:', !!response.data);

        if (!rememberMe && response.success && response.data) {
          const isPetPro = response.data.customerTypeID === CustomerType.PET_PRO;
          console.log('🍪 [LoginForm] Creating session cookie for isPetPro:', isPetPro);
          const removeResult = removeSessionCookie();
          console.log('✅ [LoginForm] Session cookie removal result before creation:', removeResult);
          setSessionCookie(isPetPro);
          console.log('✅ [LoginForm] Session cookie creation requested');
        }

        // here, just remove the session cookie if rememberMe is not checked
        if (!!rememberMe && response.success && response.data) {
          console.log('🗑️ [LoginForm] RememberMe is checked - removing any existing session cookie');
          const removeResult = removeSessionCookie();
          console.log('✅ [LoginForm] Session cookie removal result:', removeResult);
        }

        if (response.success && response.data) {
          setIsVerified(true);
          // Clear any previous form errors on successful login
          setFormErrors({});
          // Show verified state briefly before proceeding
          setTimeout(() => {
            if (onSubmit) {
              // Backoffice: block suspended users
              if (isBackoffice && response.data.customerStatusID === CustomerStatus.Suspended) {
                toast(
                  ({ closeToast }) => (
                    <AuthToast
                      type="error"
                      message="Your account has been suspended. Please contact support service at 855-940-5234 or support@pawtree.com to resolve this matter."
                      closeToast={closeToast}
                    />
                  ),
                  { containerId: AUTH_MODAL_TOAST_CONTAINER }
                );
                setIsSubmitting(false);
                setIsVerified(false);
                return;
              }
              // Check if device is trusted - if true, skip all 2FA modals
              const isDeviceTrusted = response.data?.isDeviceTrusted === true;

              // Only show 2FA modals if device is NOT trusted
              const shouldShow2FA = !isDeviceTrusted && response.data?.is2FAVisited === false;
              const shouldEnter2FA = !isDeviceTrusted && response.data?.is2FAEnabled === true;

              try {
                if (shouldEnter2FA && typeof window !== 'undefined' && response.data?.customerId) {
                  localStorage.setItem('__pending_2fa_customer_id', String(response.data.customerId));
                  localStorage.setItem('__pending_2fa_remember_me', String(rememberMe));
                }
              } catch (e) {}
              if (response.data.customerTypeID === CustomerType.PET_PRO) {
                onSubmit({
                  rememberMe,
                  returnUrl: getClientEnv().BACKOFFICE_ORIGIN,
                  shouldShow2FA,
                  shouldEnter2FA,
                  customerTypeID: response.data.customerTypeID,
                });
              } else {
                onSubmit({
                  rememberMe,
                  returnUrl: response.data.redirectUrl,
                  shouldShow2FA,
                  shouldEnter2FA,
                  customerTypeID: response.data.customerTypeID,
                });
              }
            }
          }, 1500);
        } else {
          setIsSubmitting(false);
          // Handle error message from API
          const errorMessage = response.errorMessage || response.message || 'Login failed. Please try again.';
          // Set form errors to show error states on fields
          setFormErrors({
            email: SHOW_ERROR_STATE_ONLY, // Show red border but no error message
            password: LOGIN_FAILED_ERROR,
          });
          if (onError) {
            onError(errorMessage);
          } else {
            // Show toast directly if no onError callback provided (e.g., Backoffice login)
            toast(({ closeToast }) => <AuthToast type="error" message={errorMessage} closeToast={closeToast} />, {
              containerId: AUTH_MODAL_TOAST_CONTAINER,
            });
          }
        }
      } catch (error: any) {
        setIsSubmitting(false);
        console.error('Login failed:', error);
        // Handle API errors
        const errorMessage =
          error?.data?.message ||
          error?.data?.exceptionMessage ||
          error?.message ||
          'An error occurred during login. Please try again.';
        // Set form errors to show error states on fields
        setFormErrors({
          email: SHOW_ERROR_STATE_ONLY, // Show red border but no error message
          password: LOGIN_FAILED_ERROR,
        });
        if (onError) {
          onError(errorMessage);
        } else {
          // Show toast directly if no onError callback provided (e.g., Backoffice login)
          toast(({ closeToast }) => <AuthToast type="error" message={errorMessage} closeToast={closeToast} />, {
            containerId: AUTH_MODAL_TOAST_CONTAINER,
          });
        }
      }
    });
  };

  // Handler for third-party login (Google, Facebook, Apple)
  /* eslint-disable sonarjs/cognitive-complexity */
  const handleThirdPartyLogin = async (
    thirdPartyProvider: number,
    thirdPartyToken: string,
    userData?: { email?: string; firstName?: string; lastName?: string; picture?: string }
  ) => {
    try {
      setIsSubmitting(true);
      const response = await loginMutation({
        thirdPartyProvider,
        thirdPartyToken,
        rememberMe,
      }).unwrap();

      if (response.success && response.data) {
        // Same success handling as regular login for existing users
        setTimeout(() => {
          setIsSubmitting(false);
          setIsVerified(true);
          setTimeout(() => {
            if (response.data.customerStatusID === CustomerStatus.Terminated) {
              const errorMessage = 'Your account has been terminated. Please contact support.';
              toast(({ closeToast }) => <AuthToast type="error" message={errorMessage} closeToast={closeToast} />, {
                containerId: AUTH_MODAL_TOAST_CONTAINER,
              });
              setIsSubmitting(false);
              setIsVerified(false);
              return;
            }

            // Check if device is trusted - if true, skip all 2FA modals
            const isDeviceTrusted = response.data?.isDeviceTrusted === true;

            // Only show 2FA modals if device is NOT trusted
            const shouldShow2FA = !isDeviceTrusted && response.data?.is2FAVisited === false;
            const shouldEnter2FA = !isDeviceTrusted && response.data?.is2FAEnabled === true;

            try {
              if (shouldEnter2FA && typeof window !== 'undefined' && response.data?.customerId) {
                localStorage.setItem('__pending_2fa_customer_id', String(response.data.customerId));
                localStorage.setItem('__pending_2fa_remember_me', String(rememberMe));
              }
            } catch (e) {}

            if (response.data.customerTypeID === CustomerType.PET_PRO) {
              onSubmit?.({
                rememberMe,
                returnUrl: getClientEnv().BACKOFFICE_ORIGIN,
                shouldShow2FA,
                shouldEnter2FA,
                customerTypeID: response.data.customerTypeID,
              });
            } else {
              const redirectUrl = isBackoffice ? getClientEnv().REPLICATED_URL : response.data.redirectUrl;
              onSubmit?.({
                rememberMe,
                returnUrl: redirectUrl,
                shouldShow2FA,
                shouldEnter2FA,
                customerTypeID: response.data.customerTypeID,
              });
            }
          }, 1500);
        }, 0);
      } else {
        // Handle isNewUser case when response.success is false
        if (response.data?.isNewUser === true) {
          setIsSubmitting(false);
          setIsVerified(false);

          // Redirect to signup flow with third-party data
          if (onThirdPartyNewUser && userData) {
            const providerMap: Record<number, string> = { 0: GOOGLE, 1: FACEBOOK, 2: APPLE };
            onThirdPartyNewUser({
              token: thirdPartyToken,
              type: providerMap[thirdPartyProvider] || GOOGLE,
              email: userData.email || '',
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
              picture: userData.picture || '',
            });
          }
          return;
        }

        // Show error for other failure cases
        setIsSubmitting(false);
        const errorMessage = response.message || response.errorMessage || 'Third-party login failed';
        if (onError) {
          onError(errorMessage);
        } else {
          toast(({ closeToast }) => <AuthToast type="error" message={errorMessage} closeToast={closeToast} />, {
            containerId: AUTH_MODAL_TOAST_CONTAINER,
          });
        }
      }
    } catch (error: any) {
      setIsSubmitting(false);
      const errorMessage = error?.data?.message || error?.data?.errorMessage || 'Third-party login failed';
      // eslint-disable-next-line sonarjs/no-duplicate-string
      toast.dismiss('third-party-login-error');
      toast(({ closeToast }) => <AuthToast type="error" message={errorMessage} closeToast={closeToast} />, {
        containerId: AUTH_MODAL_TOAST_CONTAINER,
        // eslint-disable-next-line sonarjs/no-duplicate-string
        toastId: 'third-party-login-error',
      });
      if (onError) {
        onError(errorMessage);
      }
    }
  };

  // Pass the third-party login handler to parent component
  React.useEffect(() => {
    if (onThirdPartyLogin) {
      onThirdPartyLogin(handleThirdPartyLogin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const forgotPasswordLink = isBackoffice ? '/forgotpassword' : generateURL('/account/forgotpassword', webalias);

  return (
    <div className="">
      <h2 className="v2-font-sweet-sans-pro-bold v2-font-bold v2-text-3xl md:v2-text-3xl v2-text-[#101828] v2-mb-4 md:v2-mb-8">
        Welcome
      </h2>
      <div className="v2-flex v2-flex-col v2-gap-5 v2-mb-6">
        <div className="v2-grid v2-grid-cols-1 v2-gap-5">
          <div>
            <label
              htmlFor="email"
              className="v2-block v2-mb-[6px] v2-text-[#344054] v2-text-sm v2-font-sweet-sans-pro-medium v2-font-medium"
            >
              Email or Username
            </label>
            <TextInput
              type="text"
              name="email"
              placeholder="Email or Username"
              inputClassName={`v2-font-sweet-sans-pro !v2-py-[7px] ${
                errors.email
                  ? '!v2-border-[#FDA29B] focus:!v2-border-[#FDA29B] focus:!v2-shadow-none'
                  : isVerified
                  ? '!v2-border-[#12B76A] focus:!v2-border-[#12B76A] focus:!v2-shadow-none !v2-bg-[#C5D1CC]'
                  : ''
              }`}
              value={data.email}
              onChange={handleChange}
              rightIconComponent={
                errors.email ? (
                  <Image src={AlertCircle} alt="Error" className="v2-absolute v2-right-3 v2-top-[11px] v2-w-5 v2-h-5" />
                ) : isVerified ? (
                  <Image
                    src={CheckIcon}
                    alt="Verified"
                    className="v2-absolute v2-right-3 v2-top-[11px] v2-w-5 v2-h-5"
                  />
                ) : undefined
              }
            />
            {errors.email && (
              <p className="v2-font-sweet-sans-pro v2-text-sm v2-font-light v2-text-[#D92D20]">{errors.email}</p>
            )}
          </div>
        </div>
        <PasswordInput
          name="password"
          label="Password"
          value={data.password}
          onChange={handleChange}
          error={errors.password}
          isVerified={isVerified}
          inputClassName="!v2-py-[7px]"
        />
      </div>
      <div className="v2-flex v2-items-center v2-justify-between v2-mb-6">
        <label className="v2-flex v2-items-center v2-cursor-pointer">
          <input
            type="checkbox"
            className="v2-mr-2 v2-cursor-pointer"
            checked={rememberMe}
            onChange={e => setRememberMe(e.target.checked)}
          />
          <span className="v2-text-sm v2-text-[#344054]">Remember me</span>
        </label>
        <ButtonLink
          scheme={'primary'}
          variant={'outlined'}
          className="!v2-border-0 !v2-p-0 !v2-rounded-0 v2-font-sweet-sans-pro-bold v2-font-bold v2-text-sm !v2-w-fit"
          href={forgotPasswordLink}
        >
          Forgot password?
        </ButtonLink>
      </div>
      <Button
        btnSize="md"
        scheme="primary"
        variant="filled"
        type="button"
        className="v2-font-sweet-sans-pro-bold v2-font-bold v2-w-full v2-mb-3 v2-flex v2-items-center v2-justify-center v2-gap-2 !v2-py-[9px]"
        onClick={handleSubmit}
        disabled={isSubmitting && !isVerified}
      >
        {isVerified ? 'Verified' : isSubmitting ? 'Verifying' : 'Sign In'}
        {isVerified ? (
          <Image src={VerifiedTick} alt="Verified" className="v2-w-5 v2-h-5" />
        ) : isSubmitting ? (
          <Spinner height={20} />
        ) : null}
      </Button>
      <div className="v2-grid v2-grid-cols-3 v2-gap-3 v2-mb-6">
        <GoogleSignInButton
          onSuccess={data => {
            // Check if email is present
            if (!data.email || data.email.trim() === '') {
              toast(
                ({ closeToast }) => (
                  <AuthToast
                    type="error"
                    message="Email not found. Please use a provider that includes an email."
                    closeToast={closeToast}
                  />
                ),
                { containerId: AUTH_MODAL_TOAST_CONTAINER }
              );
              return;
            }

            // For login: data contains token and type, call handleThirdPartyLogin
            if (data.token && data.type) {
              // Convert type string to provider number: "Google" -> 0, "Facebook" -> 1, "Apple" -> 2
              const providerMap: Record<string, number> = { google: 0, facebook: 1, apple: 2 };
              const provider = providerMap[data.type] ?? 0;
              // Pass user data for new user handling
              handleThirdPartyLogin(provider, data.token, {
                email: data.email,
                firstName: data.firstName,
                lastName: data.lastName,
                picture: data.picture,
              });
            }
          }}
        />
        <FacebookSignInButton
          onSuccess={data => {
            // Check if email is present
            if (!data.email || data.email.trim() === '') {
              toast(
                ({ closeToast }) => (
                  <AuthToast
                    type="error"
                    message="Email not found. Please use a provider that includes an email."
                    closeToast={closeToast}
                  />
                ),
                { containerId: AUTH_MODAL_TOAST_CONTAINER }
              );
              return;
            }

            if (data.token && data.type) {
              const providerMap: Record<string, number> = { Google: 0, Facebook: 1, Apple: 2 };
              const provider = providerMap[data.type] ?? 1;
              handleThirdPartyLogin(provider, data.token, {
                email: data.email,
                firstName: data.firstName,
                lastName: data.lastName,
                picture: data.picture,
              });
            }
          }}
        />
        <AppleSignInButton
          isBackoffice={isBackoffice}
          onSuccess={data => {
            // Check if email is present
            if (!data.email || data.email.trim() === '') {
              toast(
                ({ closeToast }) => (
                  <AuthToast
                    type="error"
                    message="Email not found. Please use a provider that includes an email."
                    closeToast={closeToast}
                  />
                ),
                { containerId: AUTH_MODAL_TOAST_CONTAINER }
              );
              return;
            }

            if (data.token && data.type) {
              const providerMap: Record<string, number> = { Google: 0, Facebook: 1, Apple: 2 };
              const provider = providerMap[data.type] ?? 2;
              handleThirdPartyLogin(provider, data.token, {
                email: data.email,
                firstName: data.firstName,
                lastName: data.lastName,
                picture: data.picture,
              });
            }
          }}
        />
      </div>
      {!isBackoffice && (
        <div className="v2-flex v2-items-center v2-justify-center">
          <p className="v2-text-[#101828] v2-text-base md:v2-text-[22px] v2-font-sweet-sans-pro v2-font-normal v2-flex v2-items-center v2-gap-1 v2-whitespace-nowrap">
            Don&apos;t have an account?
            <ButtonLink
              scheme={'primary'}
              variant={'outlined'}
              className="!v2-border-0 !v2-p-0 !v2-rounded-0 v2-font-sweet-sans-pro v2-font-normal !v2-text-base md:!v2-text-[22px] !v2-w-fit v2-whitespace-nowrap"
              onClick={onToggleSignup}
            >
              Sign up
            </ButtonLink>
          </p>
        </div>
      )}
    </div>
  );
};

export default LoginForm;
